/**
 * safeShellT — Translation guard for tenant-user shell labels (TRP-414).
 *
 * WHAT
 * Wraps i18next's t() with strict fallback behavior to prevent raw i18n
 * keys (shell.*, nav.*, route.*, breadcrumb.*) from leaking into visible
 * DOM, aria-label, title, alt, placeholder, or document.title.
 *
 * WHY
 * react-i18next's default miss-behavior returns the key path verbatim,
 * so a missing translation surfaces as "shell.nav.item.dashboard" in the
 * DOM. That is unacceptable for the shell — under Arabic locale it would
 * leak English raw-key text into RTL chrome. This guard enforces:
 *   - null/undefined → fallback
 *   - non-string return → fallback
 *   - empty string → fallback
 *   - returned value equals key (i18next miss) → fallback
 *   - returned value starts with any raw-key prefix → fallback
 *   - otherwise return the translated value verbatim
 *
 * HOW
 * Use at every shell render site (AppSidebar, AppHeader, routeMetadata,
 * breadcrumb consumer). Raw t() calls at shell render sites are NOT
 * acceptable under TRP-414.
 *
 * Usage:
 *   const { t } = useTranslationWithCMS();
 *   const label = safeShellT(t, 'shell.nav.item.dashboard', { fallback: item.label });
 *
 *   With interpolation:
 *   const aria = safeShellT(t, 'shell.starred.add.aria', {
 *     fallback: `Star ${label}`,
 *     values: { label },
 *   });
 */

/**
 * Loose translation-function type accepted by safeShellT.
 *
 * Matches both i18next's full `TFunction` AND the looser callback returned
 * by `useTranslationWithCMS` (which wraps i18nT with CMS-override merging
 * and returns a structural function — not branded as i18next's TFunction).
 * Accepting this looser shape keeps the resolver consumable by the CMS
 * hook without losing strict-mode TypeScript safety at the call site.
 */
export type SafeShellTFunction = (
  key: string,
  options?: Record<string, unknown>,
) => unknown;

/**
 * Raw-key prefixes guarded against. Any value starting with one of these
 * is treated as an unresolved key and replaced with the fallback.
 *
 * Keep in sync with i18n/__tests__/raw-key-leak.test.ts regex.
 */
const RAW_KEY_PREFIXES = [
  'shell.',
  'nav.',
  'route.',
  'breadcrumb.',
] as const;

export interface SafeShellTOptions {
  /**
   * i18next interpolation values, e.g. { label: 'Dashboard' }.
   * Forwarded to t() as the options argument.
   */
  readonly values?: Record<string, unknown>;
  /**
   * Fallback returned when the resolver detects a raw-key or unresolved
   * lookup. Defaults to empty string. Callers should provide a governed
   * fallback (e.g. the legacy `label` field from nav.ts under EN locale,
   * or an explicit operator-approved AR placeholder under AR locale).
   */
  readonly fallback?: string;
}

/**
 * Safe shell translation resolver. See module docstring.
 */
export function safeShellT(
  t: SafeShellTFunction,
  key: string,
  options?: SafeShellTOptions,
): string {
  const fallback = options?.fallback ?? '';

  let raw: unknown;
  try {
    raw = t(key, options?.values);
  } catch {
    return fallback;
  }

  if (raw == null) return fallback;
  if (typeof raw !== 'string') return fallback;
  if (raw === '') return fallback;
  if (raw === key) return fallback;
  for (const prefix of RAW_KEY_PREFIXES) {
    if (raw.startsWith(prefix)) return fallback;
  }
  return raw;
}

// Exported for tests + the raw-key-leak guard's runtime check.
export const SAFE_SHELL_T_RAW_KEY_PREFIXES = RAW_KEY_PREFIXES;
